import { STATUS_THRESHOLDS } from '@/config/constants';
import { generateRecommendation } from './insight-generator';
import type {
  Answer,
  ScoringCategory,
  CategoryScore,
  ScoringResult,
  Status,
  Difficulty
} from '@/types';
import type { ProductScoringConfig } from '@/types/product';

export class ScoringEngine {
  private answers: Answer[];
  private config: ProductScoringConfig;

  constructor(answers: Answer[], config: ProductScoringConfig) {
    this.answers = answers;
    this.config = config;
  }

  calculate(): ScoringResult {
    const categoryScores = this.calculateCategoryScores();
    const totalScore = categoryScores.reduce((sum, c) => sum + c.earnedPoints, 0);
    const totalPercentage = Math.round((totalScore / this.config.maxTotal) * 100);
    const projectedScoreAfterInova = this.calculateAfterInovaScore(categoryScores);
    const finalRecommendation = this.determineFinalRecommendation(totalPercentage);
    const detailedAnalysis = this.generateDetailedAnalysis(categoryScores);

    return {
      totalScore,
      totalPercentage,
      projectedScoreAfterInova,
      categoryScores,
      finalRecommendation,
      detailedAnalysis
    };
  }

  private calculateCategoryScores(): CategoryScore[] {
    return this.config.categories.map(category => {
      const maxPoints = this.config.weights[category] ?? 0;
      const categoryAnswers = this.answers.filter(a => {
        return this.config.questionCategories[a.questionId] === category;
      });

      const earnedPoints = maxPoints > 0
        ? Math.min(categoryAnswers.reduce((sum, a) => sum + a.score, 0), maxPoints)
        : 0;

      const percentage = maxPoints > 0 ? Math.round((earnedPoints / maxPoints) * 100) : 0;
      const isNonScoring = this.config.nonScoringCategories.includes(category);
      const status = isNonScoring ? 'ok' : this.determineStatus(percentage);
      const difficulty = isNonScoring ? 'none' : this.determineDifficulty(percentage, category);
      const recommendation = isNonScoring ? '' : generateRecommendation(category, categoryAnswers, percentage, this.config.recommendationMaps);

      return {
        category,
        label: this.config.labels[category] ?? category,
        maxPoints,
        earnedPoints,
        percentage,
        status,
        recommendation,
        difficulty
      };
    });
  }

  private determineStatus(percentage: number): Status {
    if (percentage >= STATUS_THRESHOLDS.OK) return 'ok';
    if (percentage >= STATUS_THRESHOLDS.ATTENTION) return 'attention';
    return 'critical';
  }

  private determineDifficulty(percentage: number, category: ScoringCategory): Difficulty {
    if (percentage >= 80) return 'none';

    let difficulty: Difficulty;
    if (percentage < 30) difficulty = 'hard';
    else if (percentage < 65) difficulty = 'moderate';
    else difficulty = 'easy';

    const cap = this.config.maxDifficulty?.[category];
    if (cap) {
      const order: Difficulty[] = ['easy', 'moderate', 'hard'];
      if (order.indexOf(difficulty) > order.indexOf(cap)) {
        difficulty = cap;
      }
    }

    return difficulty;
  }

  private calculateAfterInovaScore(categoryScores: CategoryScore[]): number {
    let projectedTotal = 0;

    for (const categoryScore of categoryScores) {
      const maxPossible = this.config.weights[categoryScore.category] ?? 0;
      if (maxPossible === 0) continue;
      const improvementFactor = this.config.improvements[categoryScore.category] ?? 1.0;
      const improved = maxPossible * improvementFactor;
      projectedTotal += Math.max(categoryScore.earnedPoints, improved);
    }

    return Math.min(Math.round((projectedTotal / this.config.maxTotal) * 100), 100);
  }

  private determineFinalRecommendation(percentage: number): string {
    const [high, medium] = this.config.scoreThresholds ?? [70, 40];
    if (percentage >= high) return this.config.finalRecommendations[0];
    if (percentage >= medium) return this.config.finalRecommendations[1];
    return this.config.finalRecommendations[2];
  }

  private generateDetailedAnalysis(categoryScores: CategoryScore[]): string[] {
    const analysis: string[] = [];

    const scoringCategories = categoryScores.filter(
      c => !this.config.nonScoringCategories.includes(c.category)
    );

    const criticalCategories = scoringCategories.filter(c => c.status === 'critical');
    const attentionCategories = scoringCategories.filter(c => c.status === 'attention');
    const okCategories = scoringCategories.filter(c => c.status === 'ok');

    if (okCategories.length > 0) {
      analysis.push(
        `Pontos fortes identificados: ${okCategories.map(c => c.label).join(', ')}.`
      );
    }

    if (attentionCategories.length > 0) {
      analysis.push(
        `Áreas que precisam de atenção: ${attentionCategories.map(c => c.label).join(', ')}.`
      );
    }

    if (criticalCategories.length > 0) {
      analysis.push(
        `Áreas críticas que necessitam intervenção imediata: ${criticalCategories.map(c => c.label).join(', ')}.`
      );
    }

    return analysis;
  }
}
